import { useMemo, useState } from "react";

import { ActionsModal, Icon, RenderWhen } from "@components";
import PillSwitchNew from "@components/PillSwitchNew";
import VerticalDivider from "@components/VerticalDivider";
import { useDataModal } from "@js2/hooks";
import NetworkThreatCard from "@modules/network/components/cards/NetworkThreatCard";
import CardTitle from "@modules/network/components/common/CardTitle";
import FilesTable from "@modules/network/ThreatIntel/common/tables/FilesTable";
import { LOCATION } from "@modules/search/consts";
import { SelectRowsProvider } from "@modules/search/enhancers";
import { useReferencingFiles } from "../hooks";
import { getSchema } from "./schema";

type Props = {
  url: string;
  title?: string;
};

const FilesReferencingCard = ({
  url,
  title = "Files Referencing this Domain"
}: Props) => {
  const [location, setLocation] = useState(LOCATION.CLOUD);
  const { data: modal, onToggle } = useDataModal();

  const { data, isPending, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useReferencingFiles({ url, location });

  const entries = data?.pages?.flatMap(page => page?.data ?? []) ?? [];

  const handleNextPage = () => {
    if (!hasNextPage || isFetchingNextPage) return;
    fetchNextPage();
  };

  const schema = useMemo(() => getSchema(onToggle), [onToggle]);

  return (
    <NetworkThreatCard
      header={
        <NetworkThreatCard.Header
          title={
            <div className="flex align-center justify-content-space-between">
              <CardTitle
                label={title}
                // TODO popraviti, ivica pokvario :D
                hint="Files that reference this domain (extracted by Spectra Core)."
              />
              <VerticalDivider />
              <PillSwitchNew
                handleFilterChange={(value: string) => setLocation(value)}
                activeValue={location}
              >
                <PillSwitchNew.Item
                  value={LOCATION.LOCAL}
                  data-testid="domain-files-referencing-toggle-local"
                >
                  <Icon name="local" />
                  Local
                </PillSwitchNew.Item>
                <PillSwitchNew.Item
                  value={LOCATION.CLOUD}
                  data-testid="domain-files-referencing-toggle-cloud"
                >
                  <Icon name="cloud" />
                  Cloud
                </PillSwitchNew.Item>
              </PillSwitchNew>
            </div>
          }
        />
      }
      contentClassName="p-0 flex flex-center justify-content-center"
      content={
        <SelectRowsProvider key={location}>
          <FilesTable
            data={entries}
            schema={schema}
            isLoading={isPending}
            isFetchingNextPage={isFetchingNextPage}
            hasMore={hasNextPage}
            onNextPage={handleNextPage}
          />
          <RenderWhen condition={Boolean(modal.activeModal)}>
            <ActionsModal
              modalName={modal.activeModal}
              activeModalData={modal.activeModalData}
              toggleModal={onToggle}
            />
          </RenderWhen>
        </SelectRowsProvider>
      }
    />
  );
};

export default FilesReferencingCard;
